import React, { useEffect } from 'react';
import clsx from 'clsx';
import styles from './button.module.scss';

import {
  ArrowPressHandler,
  FocusHandler,
  useFocusable
} from '@noriginmedia/norigin-spatial-navigation';

import { useAtomValue } from 'jotai';
import { breadcrumbUrlContainsPathname } from '../player/_utils/navigation-utils';
import { isHeroRenderedAtom } from '@smart-tv/atoms';

export interface ButtonProps {
  focusKey: string | undefined;
}

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * The onEnterPress callback is fired when the enter button is clicked, while the button is in focus
   */
  onEnterPress?: () => void;
  onArrowPress?: ArrowPressHandler<ButtonProps>;
  focusKey?: string;
  selected?: boolean;
  disabled?: boolean;
  size?: 'sm';
  icon?: React.ReactNode;
  staticText?: boolean;
  onNavFocus?: FocusHandler;
  onNavBlur?: FocusHandler;
  expandOnFocus?: boolean;
  isFocused?: boolean;
  variant?: 'button' | 'header' | 'tab' | 'outline' | 'text';
}

const Button: React.FC<Props> = (props: Props) => {
  const {
    children,
    type = 'button',
    onEnterPress,
    focusKey: focusKeyParam,
    onArrowPress,
    selected,
    disabled,
    size,
    icon,
    staticText,
    onNavFocus,
    onNavBlur,
    expandOnFocus = false,
    isFocused = false,
    variant = 'button',
    className,
    ...rest
  } = props;

  const handleEnterPress = React.useCallback(() => {
    if (ref.current && typeof ref.current.click === 'function') {
      ref.current.click();
    }

    onEnterPress?.();
    // TODO
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { ref, focused, focusSelf } = useFocusable({
    onFocus: onNavFocus,
    onBlur: onNavBlur,
    onEnterPress: handleEnterPress,
    focusKey: focusKeyParam,
    onArrowPress: onArrowPress,
    extraProps: { focusKey: focusKeyParam },
    focusable: !disabled
  });

  const isStripeHeroRendered = useAtomValue(isHeroRenderedAtom);
  const isUniverseOpen = breadcrumbUrlContainsPathname('universe');
  const [isHovering, setIsHovering] = React.useState(false);
  const showFocusedHeaderEleStyling = !isUniverseOpen || isStripeHeroRendered;
  const isHeader = variant === 'header';
  const isTab = variant === 'tab';
  const isOutline = variant === 'outline';
  const isText = variant === 'text';
  const hasChildren = React.Children.toArray(children).some(
    (child) => typeof child !== 'string' || child.trim() !== ''
  );
  const shouldRenderText =
    isHeader && staticText === false
      ? (focused || selected || isHovering) && hasChildren
      : hasChildren;

  const iconComponent = React.useMemo(() => {
    if (icon) {
      return React.cloneElement(icon as React.ReactElement, {
        className: clsx(styles['button-icon'], {
          [styles['button-icon--with-text']]: shouldRenderText
        })
      });
    }
    return null;
  }, [icon, shouldRenderText]);

  useEffect(() => {
    if (isFocused) {
      focusSelf();
    }
  }, [isFocused, focusSelf]);

  const buttonClass = clsx(
    styles['button'],
    {
      [styles['button--tab']]: isTab,
      [styles['button--header']]: isHeader,
      [styles['button--outline']]: isOutline,
      [styles['button--text']]: isText,
      [styles['button--expand']]: expandOnFocus,
      [styles['button--selected']]: selected && !focused,
      [styles['button--focused']]: isHeader ? showFocusedHeaderEleStyling && focused : focused,
      [styles[`button--${size}`]]: size,

      // All classes without the styles object, are added to support adaptations via :global.scss
      'button--tab': isTab,
      'button--header': isHeader,
      'button--outline': isOutline,
      'button--text': isText,
      'button--expand': expandOnFocus,
      'button--selected': selected && !focused,
      'button--focused': isHeader ? showFocusedHeaderEleStyling && focused : focused,
      [`button--${size}`]: size
    },
    className
  );

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled}
      className={buttonClass}
      onPointerEnter={() => setIsHovering(true)}
      onPointerLeave={() => setIsHovering(false)}
      {...rest}
    >
      <span className={styles['button__text']}>
        {iconComponent}
        {shouldRenderText ? children : null}
      </span>
    </button>
  );
};

Button.displayName = 'Button';
export default Button;
